/**
 * Estado del carrito de pagos del residente.
 *
 * Hook simple `useSelectedPayments` con un Set de payment IDs. Se usa tanto
 * en `Expensas.jsx` (single-edificio) como en `MisPagos.jsx` (cross-edificio).
 * Sin context global porque cada pantalla tiene su lista propia — limpiamos
 * al desmontar.
 */
import { useCallback, useMemo, useState } from "react";

/**
 * @param {Array} availablePayments — array de payments visibles en la pantalla;
 *   el carrito se sincroniza para no quedar con IDs huérfanos al cambiar la
 *   lista visible (ej. filtros).
 * @returns {Object} API del carrito
 */
export function useSelectedPayments(availablePayments = []) {
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const visibleIds = useMemo(
    () => new Set(availablePayments.map((p) => p.id)),
    [availablePayments]
  );

  // Sincronizar selectedIds para que solo contenga IDs todavía visibles.
  const effectiveSelected = useMemo(() => {
    const out = new Set();
    selectedIds.forEach((id) => {
      if (visibleIds.has(id)) out.add(id);
    });
    return out;
  }, [selectedIds, visibleIds]);

  const toggle = useCallback((paymentId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(paymentId)) next.delete(paymentId);
      else next.add(paymentId);
      return next;
    });
  }, []);

  const isSelected = useCallback(
    (paymentId) => effectiveSelected.has(paymentId),
    [effectiveSelected]
  );

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(availablePayments.map((p) => p.id)));
  }, [availablePayments]);

  const clear = useCallback(() => setSelectedIds(new Set()), []);

  const selectedPayments = useMemo(
    () => availablePayments.filter((p) => effectiveSelected.has(p.id)),
    [availablePayments, effectiveSelected]
  );

  const totalAmount = useMemo(() => {
    return selectedPayments.reduce((sum, p) => {
      const remaining = Number(p.remainingAmount);
      const amount =
        !isNaN(remaining) && remaining > 0 ? remaining : Number(p.amount) || 0;
      return sum + amount;
    }, 0);
  }, [selectedPayments]);

  return {
    selectedIds: effectiveSelected,
    selectedPayments,
    totalAmount,
    count: effectiveSelected.size,
    isSelected,
    toggle,
    selectAll,
    clear,
  };
}
